import { computed, inject, Injectable, signal } from '@angular/core';

import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { LayerVisibilityButtonState } from '@connect-services/client/components/visibility-button/visibility-button.component';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapLayerService } from '@connect-services/client/services/map-layer.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SourcesService } from '@connect-services/client/services/sources.service';

export enum SourceTypeEnum {
    FIELD_INTERVENTION = 'FIELD_INTERVENTION',
    LAYER_SOURCE = 'LAYER_SOURCE',
    URL_LAYER_SOURCE = 'URL_LAYER_SOURCE',
}

@Injectable({ providedIn: 'root' })
export class SourcesVisibilityService {
    public readonly visibilityBySourceId = computed<Record<string, boolean>>(() => {
        MapSourceService.checkSourcesVisibility();
        MapSourceService.checkSourcesZoom();

        const result: Record<string, boolean> = {};
        const overrides = this.userVisibility();

        this.sourcesService.sources().forEach((source) => {
            result[source.sourceId] = overrides[source.sourceId] ?? this.mapSourceService.isSourceVisible(source);
        });

        return result;
    });

    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly mapLayerService = inject(MapLayerService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly sourcesService = inject(SourcesService);
    private readonly userVisibility = signal<Record<string, boolean>>({});

    public changeAllSourcesVisibility(visible: boolean): void {
        this.mapLayerService.areLayersVisible.set(visible);

        const inactiveSources = this.sourcesService
            .sources()
            .filter(
                (source: MapBaseSource<unknown>) => !this.mapEditModeService.activeEditLayerIds().has(source.sourceId),
            );

        this.userVisibility.update((current: Record<string, boolean>) => {
            const updated: Record<string, boolean> = { ...current };

            for (const source of inactiveSources) {
                updated[source.sourceId] = visible;
                this.mapSourceService.toggleSourceVisibility(source, visible);
            }

            return updated;
        });
    }

    public changeSourceVisibility(sources: MapBaseSource<unknown>[], isVisible: boolean): void {
        sources.forEach((source) => {
            this.userVisibility.update((currentVisibility) => ({
                ...currentVisibility,
                [source.sourceId]: isVisible,
            }));

            this.mapSourceService.toggleSourceVisibility(source, isVisible);
        });
    }

    public isAnySourceInZoomRange(sources: MapBaseSource<unknown>[]): boolean {
        MapSourceService.checkSourcesZoom();

        return sources.some((source: MapBaseSource<unknown>) => this.mapSourceService.isSourceInZoomRange(source));
    }

    public isSourceInZoomRange(source: MapBaseSource<unknown>): boolean {
        MapSourceService.checkSourcesZoom();

        return this.mapSourceService.isSourceInZoomRange(source);
    }

    public isVisible(source: MapBaseSource<unknown>): boolean {
        const overrides = this.userVisibility();
        if (overrides[source.sourceId] !== undefined) {
            return overrides[source.sourceId];
        }

        return this.visibilityBySourceId()[source.sourceId] ?? source.isVisible;
    }

    public mapVisibilityButtonState(
        canToggle: boolean,
        isVisible: boolean,
        isInZoomRange: boolean,
    ): LayerVisibilityButtonState {
        if (!isInZoomRange || !canToggle) {
            return isVisible ? LayerVisibilityButtonState.VisibleDisabled : LayerVisibilityButtonState.HiddenDisabled;
        }

        return isVisible ? LayerVisibilityButtonState.VisibleEnabled : LayerVisibilityButtonState.HiddenEnabled;
    }

    public setSourcesVisibility(sources: MapBaseSource<unknown>[], visible: boolean): void {
        this.userVisibility.update((current: Record<string, boolean>) => {
            const updated: Record<string, boolean> = { ...current };

            for (const source of sources) {
                updated[source.sourceId] = visible;
                this.mapSourceService.toggleSourceVisibility(source, visible);
            }

            return updated;
        });
    }
}
